import { useState, useEffect, useMemo } from "react"
import ReactDOM from "react-dom"
import { Info } from "lucide-react"
import { get, post } from "../../../../utils/apiClient"
import toast from "react-hot-toast"
import Pagination from "../../../common/pagination"
import PopupPracticeSelector from "./popupPracticeSelector"
import Select from "react-select"

const FaxRow = ({
  user,
  saveLoading,
  handleAssign,
  selectOptions,
  authUser,
  hoveredRow,
  activeRow,
  setHoveredRow,
  setActiveRow,
  setMoreInfo,
}) => {
  const selectedValues = useMemo(() => {
    const practices = user.assignedPractices || []
    return practices
      .map((practice) => ({
        value: practice.practiceId,
        label: practice.practiceName,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [user.assignedPractices])

  const selectedValuesString = selectedValues.map((v) => v.label).join(", ")

  const selectedValuesStringMaxThree =
    selectedValues.length > 3
      ? selectedValues.slice(0, 3).map((v) => v.label).join(", ")
      : selectedValuesString

  return (
    <tr
      className={`${hoveredRow === user.emailAddress || activeRow === user.emailAddress ? "row-highlighted" : ""}`}
      onMouseEnter={() => {
        setHoveredRow(user.emailAddress)
        if (activeRow !== user.emailAddress) setActiveRow(null)
      }}
      onMouseLeave={() => setHoveredRow(null)}
    >
      <td className="faxes-td" style={{ width: "200px" }}>
        {user.emailAddress}
      </td>
      <td className="faxes-td" style={{ width: "200px" }}>
        <span title={selectedValuesString}>
          {selectedValuesStringMaxThree}
        </span>
        {selectedValues.length > 3 && (
          <span> ... <span
            style={{ cursor: "pointer", color: "blue" }}
            onClick={() =>
              setMoreInfo({
                emailAddress: user.emailAddress,
                selectedValues,
              })
            }
          >
            More
          </span></span>
        )}
      </td>
      <td className="faxes-td" style={{ textAlign: "center" }}>
        {saveLoading === user.emailAddress ? (
          "Saving ..."
        ) : (
          <PopupPracticeSelector
            key={user.emailAddress}
            emailAddress={user.emailAddress}
            selectOptions={selectOptions}
            selectedValues={selectedValues}
            onApply={(selectedOptions) => {
              handleAssign(user, selectedOptions)
            }}
            linkLabel="Assign"
            title={`Assign Practices to ${user.emailAddress}`}
            saveLoading={saveLoading}
            authUser={authUser}
          />
        )}
      </td>
    </tr>
  )
}

const PracticeUserList = ({ authUser, api, tab }) => {
  const [users, setUsers] = useState([])
  const [appSSOUsers, setAppSSOUsers] = useState([])
  const [userPractices, setUserPractices] = useState([])
  const [selectedUserPractice, setSelectedUserPractice] = useState(null)
  const [appLoading, setAppLoading] = useState(false)
  const [faxLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchText, setSearchText] = useState("")
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [hoveredRow, setHoveredRow] = useState(null)
  const [activeRow, setActiveRow] = useState(null)
  const [moreInfo, setMoreInfo] = useState(null)
  const selectOptions = useMemo(
    () =>
      appSSOUsers.map((user) => ({
        value: user.practiceId,
        label: user.practiceName,
      })),
    [appSSOUsers],
  )

  // Search Filter
  let filteredUsers = users?.filter((faxNumber) => {
    return Object.entries(faxNumber).some(([key, value]) => {
      if (key === "emailAddress") {
        return value
          ?.toString()
          .toLowerCase()
          .includes(searchText.trim().toLowerCase())
      }
      return false
    })
  })

  if (selectedUserPractice?.value) {
    filteredUsers = filteredUsers.filter((user) =>
      user.assignedPractices?.some((practice) =>
        practice.practiceName
          ?.toLowerCase()
          .includes(selectedUserPractice?.value.toLowerCase()),
      ),
    )
  }

  // const pageSize = 10
  // const startIdx = (currentPage - 1) * pageSize
  // const currentUsers = users.slice(startIdx, startIdx + pageSize)

  const startIdx = (currentPage - 1) * rowsPerPage
  const currentUsers = filteredUsers.slice(startIdx, startIdx + rowsPerPage)
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage)

  const fetchUsers = async () => {
    try {
      // GET ALL USERS
      const data = await get(api.getUser)
      setUsers(data)

      //GET ALL PRACTICES
      const allPractices = await get(api.fetchOptions)
      setUserPractices(allPractices.map((practice) => practice.practiceName))
      setAppSSOUsers(allPractices)
    } catch (error) {
      console.error("Failed to fetch apps", error)
    } finally {
      setAppLoading(false)
    }
  }

  useEffect(() => {
    loadUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadUsers = async (appId) => {
    setAppLoading(true)
    await fetchUsers(appId)
    setAppLoading(false)
  }

  const handleAssign = async (user, selectedOptions) => {
    //Prepare New user numbers
    if (!selectedOptions) return

    setSaveLoading(user.emailAddress)

    const selectedPracticeIds = selectedOptions.map((d) => d.value)

    // const selectedUsers =
    //   selectedOptions.map((opt) => opt.value) || selectedPracticeIds || []

    // const assignPracticeIds = selectedUsers.filter(
    //   (user) => !selectedPracticeIds.includes(user)
    // )

    // const unAssignPracticeIds = selectedPracticeIds.filter(
    //   (user) => !selectedUsers.includes(user)
    // )

    // if (selectedPracticeIds.length === 0) {
    //   toast.error("Please add or remove any user", {
    //     duration: 5000,
    //
    //   })
    //   setSaveLoading(null)
    //   return
    // }

    try {
      // if (assignPracticeIds.length > 0) {
      const responseAssign = await post(api.assign, {
        emailAddress: user.emailAddress,
        practiceIds: selectedPracticeIds,
      })

      toast.success(responseAssign.message, {
        duration: 5000,
      })
      // }

      // if (unAssignPracticeIds.length > 0) {
      //   const responseUnAssign = await post(api.unAssign, {
      //     emailAddress: user.emailAddress,
      //     practiceIds: unAssignPracticeIds,
      //   })

      //   toast.success(responseUnAssign.message, {
      //     duration: 5000,
      //
      //   })
      // }

      // toast.success("Changes saved successfully", {
      //   duration: 5000,
      //
      // })

      await fetchUsers()
    } catch (error) {
      console.error("Failed to assign users", error)
      toast.error(error, {
        duration: 5000,
      })
    } finally {
      setSaveLoading(null)
    }
  }

  const userPracticeOptions = [...new Set(userPractices.map((r) => r))]
    .map((q) => ({
      value: q,
      label: q,
    }))
    .sort((a, b) => a.value.localeCompare(b.value))

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      {appLoading ? (
        <div
          className="loading-container-user-list"
          style={{ minHeight: "500px" }}
        >
          <div className="loading-message">Loading...</div>
        </div>
      ) : (
        <>
          <div
            className="faxes-content"
            style={{
              textAlign: "center",
              width: "100%",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <div
              className="search-container"
              style={{
                width: "100%",
                alignItems: "end",
                display: "flex",
                justifyContent: "space-between",
                paddingTop: "10px",
                boxSizing: "border-box",
                paddingRight: "5px",
                marginBottom: "5px",
              }}
            >
              <div className="setting-note" style={{ width: "auto" }}>
                <Info
                  size={16}
                  style={{ marginRight: 4, verticalAlign: "middle" }}
                />
                {` Only practices associated with a fax number will be available
                for ${tab} assignment.`}
              </div>

              <div style={{ display: "flex", gap: "5px", flexShrink: 0 }}>
                <Select
                  className="basic-single"
                  placeholder="Filter by Practice"
                  options={userPracticeOptions}
                  value={selectedUserPractice}
                  // onChange={setSelectedType}
                  onChange={(v) => {
                    setSelectedUserPractice(v)
                    setCurrentPage(1)
                  }}
                  isClearable
                  isSearchable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: 0,
                      minWidth: 100,
                      height: 41,
                    }),
                    option: (base) => ({
                      ...base,
                      textAlign: "left",
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />

                <div>
                  <div className="search-input-wrapper">
                    <input
                      type="text"
                      placeholder={`Search ${
                        tab === "admin" ? "Admin" : "User"
                      }...`}
                      value={searchText}
                      onChange={(e) => {
                        setSearchText(e.target.value)
                        setCurrentPage(1)
                      }}
                      style={{ width: "200px", paddingRight: "22px" }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setSearchText("")
                        setCurrentPage(1)
                      }}
                      className="search-clear-btn"
                      style={{ visibility: searchText ? "visible" : "hidden" }}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {faxLoading ? (
              <div className="loading-container-user-list">
                <div className="loading-message">Loading...</div>
              </div>
            ) : (
              <>
                <>
                  <div className="table-container">
                    <table className="faxes-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="faxes-th" style={{ width: "25%" }}>
                            {`${tab === "admin" ? "Admin" : "User"}`}
                          </th>
                          <th className="faxes-th" style={{ width: "65%" }}>
                            Practices
                          </th>
                          <th
                            className="faxes-th"
                            style={{ textAlign: "center" }}
                          >
                            Action
                          </th>
                          {/* <th
                              className="faxes-th"
                              style={{ textAlign: "center" }}
                            >
                              Action
                            </th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {currentUsers.length > 0 ? (
                          currentUsers.map((user) => (
                            <FaxRow
                              key={user.emailAddress}
                              user={user}
                              saveLoading={saveLoading}
                              handleAssign={handleAssign}
                              selectOptions={selectOptions}
                              authUser={authUser}
                              hoveredRow={hoveredRow}
                              activeRow={activeRow}
                              setHoveredRow={setHoveredRow}
                              setActiveRow={setActiveRow}
                              setMoreInfo={setMoreInfo}
                            />
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="100%"
                              style={{
                                textAlign: "center",
                                padding: "10px",
                                border: "1px solid #ccc",
                              }}
                            >
                              No data found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {filteredUsers.length > 0 && (
                    <div
                      className="faxes-pagination"
                      style={{
                        marginTop: "8px",
                        display: "flex",
                        justifyContent: "end",
                      }}
                    >
                      {filteredUsers.length > 0 && (
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          rowsPerPage={rowsPerPage}
                          totalRows={filteredUsers.length}
                          onPageChange={(page) => setCurrentPage(page)}
                          onRowsPerPageChange={(count) => {
                            setRowsPerPage(count)
                            setCurrentPage(1) // Reset to first page on change
                          }}
                        />
                      )}
                    </div>
                  )}
                </>
              </>
            )}
          </div>
        </>
      )}
      {moreInfo && (
        <InfoPopup moreInfo={moreInfo} onCancel={() => setMoreInfo(null)} />
      )}
    </div>
  )
}

export default PracticeUserList

const InfoPopup = ({ moreInfo, onCancel }) =>
  ReactDOM.createPortal(
    <div className="popup-overlay">
      <div className="popup" style={{ maxWidth: "600px" }}>
        <div className="checkbox-popup-header">
          <div className="popup-title" style={{ fontSize: "14px" }}>
            {moreInfo.emailAddress}
          </div>
          <button
            type="button"
            onClick={onCancel}
            style={{
              background: "transparent",
              border: "none",
              fontSize: 24,
              cursor: "pointer",
            }}
          >
            &times;
          </button>
        </div>
        <div style={{ padding: "20px" }}>
          <table className="faxes-table" style={{ width: "100%" }}>
            {/* <thead>
              <tr>
                <th className="faxes-th">Practice</th>
              </tr>
            </thead> */}
            <tbody>
              {moreInfo.selectedValues.map((v) => (
                <tr key={v.value}>
                  <td className="faxes-td">{v.label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>,
    document.body,
  )
